import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { currencySymbol, formatMoneyByCurrency } from "../utils/currency";
import { TIME_RANGE_OPTIONS, TimeRangeKey, rangeStart } from "../utils/dateRange";
import TimeRangeBar from "./TimeRangeBar";
import UndatedNote from "./UndatedNote";
import StatsPending from "./admin-stats/StatsPending";
import { revenueFrom, statsUrl } from "./admin-stats/api";
import { useAdminRead } from "../hooks/useAdminRead";
import { absMediaUrl } from "../utils/mediaUrl";
import type { AdminRevenueStats } from "../types/admin";

// Revenue tab — per-currency totals, quick KPIs and the Top Talent table for
// the selected window, added up on the server over the whole order history
// (GET /admin/stats/revenue, server lib/admin/stats.js). Moved out of
// pages/AdminDashboard.tsx in the Phase C split.

interface Props {
  // Shared, shell-owned time window so the selection persists across the
  // Dashboard / Revenue / All Payouts tabs.
  range: TimeRangeKey;
  setRange: (key: TimeRangeKey) => void;
}

export default function AdminRevenueTab({ range, setRange }: Props) {
  const navigate = useNavigate();

  // Scoped to the selected window by createdAt; undated paid orders count
  // only under All Time (surfaced via UndatedNote), so money is never
  // silently dropped. Currencies stay split throughout.
  const since = useMemo(() => rangeStart(range), [range]);
  const { data: revenueStats, failed, retry } = useAdminRead(statsUrl("revenue", since), revenueFrom);

  // Short label for the active window, reused in Revenue card labels + titles.
  const activeRangeLabel = TIME_RANGE_OPTIONS.find((o) => o.key === range)?.label ?? "All Time";

  if (!revenueStats) {
    return (
      <section className="fade-in">
        <TimeRangeBar value={range} onChange={setRange} />
        <StatsPending failed={failed} onRetry={retry} />
      </section>
    );
  }

  // One per-currency figure, keyed the way the table labels it (USD, EUR…).
  const rows = revenueStats.byCurrency;
  const byCurrency = (pick: (r: AdminRevenueStats["byCurrency"][number]) => number) =>
    Object.fromEntries(rows.map((r) => [r.currency.toUpperCase(), pick(r)] as [string, number]));

  return (
    <section className="fade-in">
      <TimeRangeBar value={range} onChange={setRange} />
      {range === "all" && (
        <UndatedNote count={revenueStats.undatedPaidCount} noun="paid order" />
      )}
      <div className="adm-two-col adm-mb-20" style={{ gap: 20 }}>
        {/* Currency breakdown */}
        <div className="adm-card">
          <h2 className="adm-section-title adm-mb-16">
            Revenue by Currency
          </h2>
          <table className="adm-table-light">
            <thead>
              <tr>
                <th>Currency</th>
                <th>Total Volume</th>
                <th>Platform Fees</th>
                <th>Talent Share</th>
                <th>Orders</th>
              </tr>
            </thead>
            <tbody>
              {revenueStats.byCurrency.map((s) => {
                const cur = s.currency.toUpperCase();
                return (
                  <tr key={cur} className="hover-row">
                    <td className="adm-bold">{cur}</td>
                    <td>{currencySymbol(cur)}{s.volume.toFixed(2)}</td>
                    <td className="adm-green adm-semibold">{currencySymbol(cur)}{s.fees.toFixed(2)}</td>
                    <td>{currencySymbol(cur)}{s.talentShare.toFixed(2)}</td>
                    <td>{s.count}</td>
                  </tr>
                );
              })}
              {revenueStats.byCurrency.length === 0 && (
                <tr>
                  <td colSpan={5} className="adm-center adm-p-20 adm-grey">
                    No paid orders yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Quick revenue KPIs */}
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {[
            {
              label: `Total Platform Revenue (${activeRangeLabel})`,
              value: formatMoneyByCurrency(byCurrency((s) => s.fees)),
              color: "var(--adm-green)",
            },
            {
              label: `Total Volume (${activeRangeLabel})`,
              value: formatMoneyByCurrency(byCurrency((s) => s.volume)),
              color: "",
            },
            {
              label: "Total Talent Payouts",
              value: formatMoneyByCurrency(byCurrency((s) => s.talentShare)),
              color: "var(--adm-blue)",
            },
            {
              label: "Total Paid Orders",
              value: String(revenueStats.byCurrency.reduce((a, b) => a + b.count, 0)),
              color: "",
            },
          ].map((k) => (
            <div key={k.label} className="kpi-card adm-cursor-default">
              <div>
                <div className="kpi-label">{k.label}</div>
                <div className="kpi-value" style={k.color ? { color: k.color } : {}}>
                  {k.value}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Top DJs all-time */}
      <div className="adm-card">
        <h2 className="adm-section-title adm-mb-14">
          Top Talent — Revenue ({activeRangeLabel})
        </h2>
        <div className="adm-table-wrapper">
          <table className="adm-table-light adm-table-compact">
            <thead>
              <tr>
                <th>#</th>
                <th>Talent</th>
                <th>Revenue (Talent Share)</th>
                <th>Platform Fee</th>
                <th>Orders</th>
                <th>Avg. Value</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {revenueStats.topTalent.map((dj, i) => {
                // Per-currency revenue / fee / avg — never blended. Fees
                // are each order's stamped commission (amount less the
                // talent's net), summed on the server.
                const src = absMediaUrl(dj.avatarUrl);
                // A deleted talent's orders share one non-clickable line.
                const isUnknown = dj.id === null;
                return (
                  <tr
                    key={dj.id ?? "unknown"}
                    className={`hover-row ${isUnknown ? "" : "clickable"}`}
                    onClick={isUnknown ? undefined : () => navigate(`/admin/dj/${dj.id}`)}
                  >
                    <td className="muted-text adm-bold">
                      {i + 1}
                    </td>
                    <td>
                      <div className="dj-cell dj-cell-compact">
                        {src ? (
                          <img src={src} className="dj-cell-avatar-sm" alt="" />
                        ) : (
                          <div className="dj-cell-placeholder-sm">{(dj.name[0] || "?").toUpperCase()}</div>
                        )}
                        <span className="dj-link-text adm-fs-82">
                          {dj.name}
                        </span>
                      </div>
                    </td>
                    <td className="adm-bold">{formatMoneyByCurrency(dj.revenueByCurrency)}</td>
                    <td className="adm-green">{formatMoneyByCurrency(dj.feeByCurrency)}</td>
                    <td>{dj.count}</td>
                    <td>{formatMoneyByCurrency(dj.avgByCurrency)}</td>
                    <td>
                      {!isUnknown && (
                        <span className="adm-fs-80" style={{ color: "var(--adm-blue)" }}>View →</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
